import React, { useState, useEffect } from "react";
import { mockClients, mockProperties } from "../data/mockBriefData";

const WA_URL =
  "https://wa.me/919289991501?text=Hello%2C%20I%20have%20reviewed%20my%20property%20brief%20and%20would%20like%20to%20book%20an%20advisor%20session.";

const PARCHMENT =
  "brightness(0) saturate(100%) invert(97%) sepia(8%) saturate(300%) hue-rotate(330deg) brightness(102%)";

/* ─── NDB Score badge ─── */
const NdbBadge = ({ score }) => (
  <div
    style={{
      position: "absolute",
      bottom: 10,
      right: 10,
      width: 44,
      height: 44,
      borderRadius: "50%",
      backgroundColor: "#C8A95A",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      lineHeight: 1,
    }}
  >
    <span
      style={{
        fontFamily: "'Cormorant Garamond', Georgia, serif",
        fontSize: 18,
        fontWeight: 700,
        color: "#2C0A0A",
      }}
    >
      {score}
    </span>
    <span
      style={{
        fontFamily: "'Jost', sans-serif",
        fontSize: 6,
        letterSpacing: "0.15em",
        color: "#2C0A0A",
        textTransform: "uppercase",
      }}
    >
      NDB
    </span>
  </div>
);

/* ─── Single property card ─── */
const PropertyCard = ({ property }) => (
  <div
    style={{
      backgroundColor: "#fff",
      border: "1px solid rgba(200,169,90,0.2)",
      borderLeft: "3px solid #C8A95A",
      borderRadius: 2,
      overflow: "hidden",
      boxShadow: "0 2px 20px rgba(44,10,10,0.06)",
    }}
  >
    {/* Image */}
    <div style={{ position: "relative", height: 240, overflow: "hidden" }}>
      <img
        src={property.imageUrl}
        alt={property.propertyName}
        loading="lazy"
        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
      />
      {/* Status badge */}
      <span
        style={{
          position: "absolute",
          bottom: 10,
          left: 10,
          backgroundColor: "rgba(44,10,10,0.85)",
          color: "#fff",
          fontFamily: "'Jost', sans-serif",
          fontSize: 8,
          letterSpacing: "0.2em",
          textTransform: "uppercase",
          padding: "4px 10px",
        }}
      >
        {property.status}
      </span>
      <NdbBadge score={property.ndbScore} />
    </div>

    {/* Body */}
    <div style={{ padding: "20px 22px 24px" }}>
      {/* Developer */}
      <p
        style={{
          fontFamily: "'Jost', sans-serif",
          fontSize: 8,
          letterSpacing: "0.3em",
          color: "#C8A95A",
          textTransform: "uppercase",
          marginBottom: 4,
        }}
      >
        {property.developer}
      </p>

      {/* Property name */}
      <h2
        style={{
          fontFamily: "'Cormorant Garamond', Georgia, serif",
          fontSize: 24,
          fontWeight: 500,
          color: "#2C0A0A",
          marginBottom: 4,
          lineHeight: 1.15,
        }}
      >
        {property.propertyName}
      </h2>

      {/* Location */}
      <p
        style={{
          fontFamily: "'Jost', sans-serif",
          fontSize: 11,
          fontWeight: 300,
          color: "#8C7E6A",
          marginBottom: 16,
        }}
      >
        {property.location}
      </p>

      {/* Gold rule */}
      <div
        style={{ width: 40, height: 1, backgroundColor: "#C8A95A", marginBottom: 16 }}
        aria-hidden="true"
      />

      {/* Financial panel */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          marginBottom: 18,
        }}
      >
        {[
          { value: property.sizeSqft, label: "SQ FT", gold: false },
          { value: property.pricePerSqft, label: "PER SQ FT", gold: false },
          { value: property.ticketSize, label: "TICKET SIZE", gold: true },
        ].map(({ value, label, gold }, i) => (
          <div
            key={label}
            style={{
              paddingLeft: i > 0 ? 14 : 0,
              borderLeft: i > 0 ? "1px solid rgba(200,169,90,0.2)" : "none",
              paddingRight: 14,
            }}
          >
            <div
              style={{
                fontFamily: "'Cormorant Garamond', Georgia, serif",
                fontSize: 20,
                fontWeight: gold ? 600 : 300,
                color: gold ? "#C8A95A" : "#2C0A0A",
                lineHeight: 1.2,
              }}
            >
              {value}
            </div>
            <div
              style={{
                fontFamily: "'Jost', sans-serif",
                fontSize: 7,
                letterSpacing: "0.2em",
                color: "#8C7E6A",
                textTransform: "uppercase",
                marginTop: 2,
              }}
            >
              {label}
            </div>
          </div>
        ))}
      </div>

      {/* Highlights */}
      <div style={{ marginBottom: 16 }}>
        {[property.highlight1, property.highlight2, property.highlight3]
          .filter(Boolean)
          .map((h, i) => (
            <div
              key={i}
              style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 6 }}
            >
              <span
                style={{
                  fontFamily: "'Jost', sans-serif",
                  fontSize: 8,
                  color: "#C8A95A",
                  lineHeight: "1.6",
                  flexShrink: 0,
                }}
              >
                ●
              </span>
              <span
                style={{
                  fontFamily: "'Libre Baskerville', Georgia, serif",
                  fontSize: 13,
                  color: "#1C1410",
                  lineHeight: 1.6,
                }}
              >
                {h}
              </span>
            </div>
          ))}
      </div>

      {/* Why this fits */}
      {property.whyThisFits && (
        <div
          style={{
            backgroundColor: "rgba(200,169,90,0.08)",
            borderLeft: "2px solid #C8A95A",
            padding: "10px 14px",
          }}
        >
          <p
            style={{
              fontFamily: "'Jost', sans-serif",
              fontSize: 7,
              letterSpacing: "0.25em",
              color: "#C8A95A",
              textTransform: "uppercase",
              marginBottom: 4,
            }}
          >
            Why this fits you
          </p>
          <p
            style={{
              fontFamily: "'Libre Baskerville', Georgia, serif",
              fontSize: 13,
              fontStyle: "italic",
              color: "#1C1410",
              lineHeight: 1.65,
            }}
          >
            {property.whyThisFits}
          </p>
        </div>
      )}
    </div>
  </div>
);

/* ─── Not found state ─── */
const BriefNotFound = () => (
  <div
    style={{
      minHeight: "100vh",
      backgroundColor: "#2C0A0A",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: "48px 24px",
      textAlign: "center",
    }}
  >
    <img
      src="/HNMB_logo_.svg"
      alt=""
      style={{ width: 64, height: "auto", filter: PARCHMENT, opacity: 0.5, marginBottom: 32 }}
    />
    <p
      style={{
        fontFamily: "'Jost', sans-serif",
        fontSize: 9,
        letterSpacing: "0.42em",
        color: "#C8A95A",
        textTransform: "uppercase",
        marginBottom: 16,
      }}
    >
      House of Numbardar
    </p>
    <h1
      style={{
        fontFamily: "'Cormorant Garamond', Georgia, serif",
        fontSize: "clamp(28px, 6vw, 44px)",
        fontWeight: 300,
        color: "#FAF6EF",
        marginBottom: 16,
        lineHeight: 1.2,
      }}
    >
      This brief is not available.
    </h1>
    <p
      style={{
        fontFamily: "'Libre Baskerville', Georgia, serif",
        fontSize: 14,
        fontStyle: "italic",
        color: "rgba(245,237,216,0.55)",
        maxWidth: 400,
        lineHeight: 1.75,
        marginBottom: 40,
      }}
    >
      The link you followed may have expired or may not have been intended for you.
      Please contact your Numbardar advisor directly.
    </p>
    <a
      href={WA_URL}
      target="_blank"
      rel="noreferrer"
      style={{
        fontFamily: "'Jost', sans-serif",
        fontSize: 10,
        letterSpacing: "0.3em",
        textTransform: "uppercase",
        color: "#2C0A0A",
        backgroundColor: "#C8A95A",
        padding: "14px 28px",
        textDecoration: "none",
        display: "inline-block",
      }}
    >
      Contact an Advisor
    </a>
  </div>
);

/* ─── Main brief page ─── */
const ClientBrief = ({ slug }) => {
  const [client, setClient] = useState(null);
  const [properties, setProperties] = useState([]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const found = mockClients.find((c) => c.slug === slug);
    if (found) {
      setClient(found);
      setProperties(mockProperties.filter((p) => p.clientId === found.id));
    }
    setReady(true);
  }, [slug]);

  useEffect(() => {
    if (client) {
      document.title = `Private Brief — ${client.name} · House of Numbardar`;
    } else if (ready) {
      document.title = "Brief Not Found · House of Numbardar";
    }
  }, [client, ready]);

  if (!ready) return null;
  if (!client) return <BriefNotFound />;

  const goldLine = (
    <div
      aria-hidden="true"
      style={{
        height: 1,
        background: "linear-gradient(to right, transparent, #C8A95A, transparent)",
        margin: "0 auto",
        width: "100%",
      }}
    />
  );

  return (
    <div style={{ fontFamily: "sans-serif", backgroundColor: "#FAF6EF" }}>

      {/* ── SECTION 1: Header band ── */}
      <div
        style={{
          backgroundColor: "#2C0A0A",
          padding: "48px 24px",
          textAlign: "center",
        }}
      >
        {goldLine}
        <div style={{ padding: "36px 0 32px" }}>
          <p
            style={{
              fontFamily: "'Jost', sans-serif",
              fontSize: 9,
              letterSpacing: "0.42em",
              color: "#C8A95A",
              textTransform: "uppercase",
              marginBottom: 20,
            }}
          >
            Private Property Brief
          </p>

          <h1
            style={{
              fontFamily: "'Cormorant Garamond', Georgia, serif",
              fontSize: 48,
              lineHeight: 1.1,
              marginBottom: 0,
            }}
          >
            <span style={{ fontWeight: 300, color: "#FAF6EF" }}>Curated</span>
            <br />
            <span style={{ fontWeight: 400, fontStyle: "italic", color: "#C8A95A" }}>
              for you.
            </span>
          </h1>

          <div
            aria-hidden="true"
            style={{
              width: 60,
              height: 1,
              backgroundColor: "#C8A95A",
              margin: "24px auto",
            }}
          />

          <p
            style={{
              fontFamily: "'Jost', sans-serif",
              fontSize: 13,
              fontWeight: 300,
              letterSpacing: "0.35em",
              color: "#FAF6EF",
              textTransform: "uppercase",
              marginBottom: 8,
            }}
          >
            {client.name}
          </p>

          <p
            style={{
              fontFamily: "'Cormorant Garamond', Georgia, serif",
              fontSize: 15,
              fontStyle: "italic",
              color: "rgba(245,237,216,0.6)",
            }}
          >
            A handpicked selection of properties — reviewed, not just listed.
          </p>
        </div>
        {goldLine}
      </div>

      {/* ── SECTION 2: Advisor note ── */}
      {client.advisorNote && (
        <div
          style={{
            backgroundColor: "#FAF6EF",
            padding: "20px 32px",
            display: "flex",
            alignItems: "center",
            gap: 16,
          }}
        >
          <div
            style={{ width: 2, height: 40, backgroundColor: "#C8A95A", flexShrink: 0 }}
            aria-hidden="true"
          />
          <div>
            <p
              style={{
                fontFamily: "'Jost', sans-serif",
                fontSize: 8,
                letterSpacing: "0.35em",
                color: "#C8A95A",
                textTransform: "uppercase",
                marginBottom: 4,
              }}
            >
              From your advisor
            </p>
            <p
              style={{
                fontFamily: "'Libre Baskerville', Georgia, serif",
                fontSize: 14,
                color: "#1C1410",
                lineHeight: 1.7,
              }}
            >
              {client.advisorNote}
            </p>
          </div>
        </div>
      )}

      {/* ── SECTION 3: Count badge ── */}
      <div style={{ backgroundColor: "#FAF6EF", padding: "32px 32px 0" }}>
        <p
          style={{
            fontFamily: "'Jost', sans-serif",
            fontSize: 9,
            letterSpacing: "0.35em",
            color: "#8C7E6A",
            textTransform: "uppercase",
          }}
        >
          {properties.length} {properties.length === 1 ? "property" : "properties"} selected for you
        </p>
      </div>

      {/* ── SECTION 4: Property cards ── */}
      <div style={{ backgroundColor: "#FAF6EF", padding: "20px 24px 40px" }}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 28,
            maxWidth: 680,
            margin: "0 auto",
          }}
        >
          {properties.map((p, i) => (
            <PropertyCard key={i} property={p} />
          ))}
        </div>
      </div>

      {/* ── SECTION 5: CTA footer ── */}
      <div
        style={{
          backgroundColor: "#2C0A0A",
          padding: "40px 32px",
          textAlign: "center",
        }}
      >
        <div
          aria-hidden="true"
          style={{ width: 60, height: 1, backgroundColor: "#C8A95A", margin: "0 auto 24px" }}
        />

        <h2
          style={{
            fontFamily: "'Cormorant Garamond', Georgia, serif",
            fontSize: 28,
            fontWeight: 300,
            color: "#FAF6EF",
            marginBottom: 8,
          }}
        >
          Ready to discuss these properties?
        </h2>

        <p
          style={{
            fontFamily: "'Jost', sans-serif",
            fontSize: 11,
            fontWeight: 200,
            letterSpacing: "0.2em",
            color: "rgba(245,237,216,0.6)",
            marginBottom: 28,
          }}
        >
          Book a private session with your Numbardar advisor.
        </p>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 12,
            alignItems: "center",
          }}
        >
          <a
            href={WA_URL}
            target="_blank"
            rel="noreferrer"
            style={{
              fontFamily: "'Jost', sans-serif",
              fontSize: 10,
              letterSpacing: "0.3em",
              textTransform: "uppercase",
              color: "#2C0A0A",
              backgroundColor: "#C8A95A",
              padding: "14px 28px",
              textDecoration: "none",
              display: "inline-block",
              width: "100%",
              maxWidth: 320,
              boxSizing: "border-box",
            }}
          >
            Book an Advisor Session
          </a>
          <a
            href="tel:+919289991501"
            style={{
              fontFamily: "'Jost', sans-serif",
              fontSize: 10,
              letterSpacing: "0.3em",
              textTransform: "uppercase",
              color: "#FAF6EF",
              backgroundColor: "transparent",
              border: "1px solid rgba(200,169,90,0.4)",
              padding: "14px 28px",
              textDecoration: "none",
              display: "inline-block",
              width: "100%",
              maxWidth: 320,
              boxSizing: "border-box",
            }}
          >
            Speak to the House →
          </a>
        </div>

        <p
          style={{
            fontFamily: "'Jost', sans-serif",
            fontSize: 9,
            letterSpacing: "0.2em",
            color: "#C8A95A",
            textTransform: "uppercase",
            marginTop: 24,
            marginBottom: 4,
          }}
        >
          houseofnumbardar.com
        </p>
        <p
          style={{
            fontFamily: "'Cormorant Garamond', Georgia, serif",
            fontSize: 11,
            fontStyle: "italic",
            color: "rgba(245,237,216,0.4)",
          }}
        >
          Legacy Since 1795
        </p>
      </div>
    </div>
  );
};

export default ClientBrief;
